// File: IntegrationsPage.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Page } from '@shopify/polaris';
import { AuthContext } from '../context/AuthContext';
import FlashCardCongrats from '../components/ui/trackerInstalled';
import OnboardingBanner from '../components/OnboardingBanner';
import styles from './IntegrationsPage.module.css';
import { markShopSetupComplete } from '../hooks/useShopifyEmbeddedRedirect';

// Icons
const CheckIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);


const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const RocketIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
    <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
  </svg>
);

const AnalyticsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18" />
    <path d="M18 17V9" />
    <path d="M13 17V5" />
    <path d="M8 17v-3" />
  </svg>
);

//const API = 'https://localhost:8000';
const API = 'https://api.baobrain.com';

export default function IntegrationsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useContext(AuthContext);
  const siteId = Number(sessionStorage.getItem('site_id')) || null;

  // State
  const [status, setStatus] = useState({ loading: true, connected: false });
  const [propsList, setPropsList] = useState([]);
  const [propsLoading, setPropsLoading] = useState(false);
  const [propId, setPropId] = useState('');
  const [savedPropId, setSavedPropId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [injectionStatus, setInjectionStatus] = useState(null);
  const [injectionMessage, setInjectionMessage] = useState('');
  const [disconnecting, setDisconnecting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Carousel slide control (0 = welcome, 1 = GA4, 2 = activate)
  const [slideIndex, setSlideIndex] = useState(0);

  // Inline confirmation toast — shows brief "Store connected" / "Tracking installed" messages
  // between slides so users feel each step "took". Pure UI, no logic dependency.
  const [toast, setToast] = useState(null); // { msg: string, key: number }
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // Fire toast when GA4 connection completes
  const prevConnectedRef = React.useRef(status.connected);
  useEffect(() => {
    if (status.loading) return;
    if (!prevConnectedRef.current && status.connected) {
      setToast({ msg: 'Google account connected successfully', key: Date.now() });
    }
    prevConnectedRef.current = status.connected;
  }, [status.connected, status.loading]);

  // Fire toast when tracking gets injected
  const prevInjectedRef = React.useRef(false);
  useEffect(() => {
    const nowInjected = injectionStatus === 'success';
    if (!prevInjectedRef.current && nowInjected) {
      setToast({ msg: 'Tracking installed automatically — no code added by you', key: Date.now() });
    }
    prevInjectedRef.current = nowInjected;
  }, [injectionStatus]);

  // Derived state
  const isInjected = injectionStatus === 'success';

  const resetIntegrationStates = () => {
    setStatus({ loading: true, connected: false });
    setPropsList([]);
    setPropsLoading(false);
    setPropId('');
    setSavedPropId('');
    setProcessing(false);
    setGeneralError('');
    setInjectionStatus(null);
    setInjectionMessage('');
    setDisconnecting(false);
  };

  // ============================================
  //  ALL ORIGINAL useEffects - PRESERVED
  // ============================================

  // Detect OAuth return and set slide accordingly
  useEffect(() => {
    const urlToken = searchParams.get('token');
    const urlShop = searchParams.get('shop');

    if (urlShop) {
      sessionStorage.setItem('shopifyShop', urlShop);
      sessionStorage.setItem('shop_domain', urlShop);
    }

    if (urlToken) {
      localStorage.setItem('token', urlToken);
      sessionStorage.setItem('token', urlToken);
      
      // Mark that we're returning from OAuth - should go to step 2
      sessionStorage.setItem('oauth_return', 'true');

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('token');
      navigate({ search: newParams.toString() }, { replace: true });

      setTimeout(() => {
        window.location.href = window.location.pathname + window.location.search;
      }, 100);
    }
  }, [searchParams, navigate]);

  // After status loads, check if we should skip to step 2
  useEffect(() => {
    if (status.loading) return;
    
    const oauthReturn = sessionStorage.getItem('oauth_return');
    
    if (oauthReturn) {
      // Clear the flag
      sessionStorage.removeItem('oauth_return');
      // Go to step 2 (GA4 connection card)
      setSlideIndex(1);
    } else if (status.connected) {
      // If already connected (returning user), start at step 2
      setSlideIndex(1);
    }
  }, [status.loading, status.connected]);

  useEffect(() => {
    async function fetchStatus() {
      try {
        const res = await fetch(`${API}/integrations/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        setStatus({ loading: false, connected: !!json.connected });
      } catch (err) {
        setStatus({ loading: false, connected: false });
        setGeneralError('Failed to connect to backend for integration status. Please try again.');
      }
    }
    if (token) fetchStatus();
    else setStatus({ loading: false, connected: false });
  }, [token]);

  useEffect(() => {
    if (!status.connected) return;
    (async () => {
      try {
        const res = await fetch(`${API}/integrations/property`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (json.propertyId) {
          setSavedPropId(json.propertyId);
          setPropId(json.propertyId);
        }
      } catch (err) {}
    })();
  }, [status.connected, token]);

  useEffect(() => {
    if (!status.connected) return;
    (async () => {
      setGeneralError('');
      setPropsLoading(true);
      try {
        const res = await fetch(`${API}/integrations/ga4-properties`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`Failed to fetch GA4 properties: ${res.status} - ${errorText}`);
        }
        const props = await res.json();
        setPropsList(props);
        if (props.length > 0) {
          setPropId((current) => current || props[0].measurementId);
        }
      } catch (e) {
        setGeneralError(`Error fetching GA4 properties: ${e.message}`);
      } finally {
        setPropsLoading(false);
      }
    })();
  }, [status.connected, token]);

  useEffect(() => {
    if (status.connected && propsList.length > 0 && !savedPropId && !propId) {
      setPropId(propsList[0].measurementId);
    }
  }, [status.connected, propsList, savedPropId, propId]);

  useEffect(() => {
    if (!token) return;
    const fetchSitesForBackfill = async () => {
      try {
        const res = await fetch(`${API}/api/sites`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (!res.ok) return;
        const json = await res.json();
        const sites = Array.isArray(json) ? json : json?.sites || json?.data || [];
        if (sites.length > 0) {
          const shopifySite = sites.find((s) => s.platform?.toLowerCase() === 'shopify');
          if (shopifySite) {
            sessionStorage.setItem('site_id', shopifySite.id.toString());
            sessionStorage.setItem('shopify_site_id', shopifySite.id.toString());
            const siteDomain = shopifySite.domain || shopifySite.url || shopifySite.shop_domain || shopifySite.shopDomain;
            if (siteDomain) {
              let cleanDomain = siteDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
              sessionStorage.setItem('shopifyShop', cleanDomain);
              sessionStorage.setItem('shop_domain', cleanDomain);
            }
          }
        }
      } catch (err) {
        console.error('[IntegrationsPage] Failed to fetch sites:', err);
      }
    };
    fetchSitesForBackfill();
  }, [token]);

  // ============================================
  //  ALL ORIGINAL HANDLERS - PRESERVED
  // ============================================

  const handleConnectGoogle = () => {
    const currentParams = new URLSearchParams(window.location.search);
    const shop = currentParams.get('shop') || sessionStorage.getItem('shopifyShop');
    const host = currentParams.get('host');
    const isInIframe = window.self !== window.top;
    const isEmbedded = !!(shop && (host || isInIframe));

    let nextPath = '/integrations';
    if (shop) {
      nextPath = `/integrations?shop=${encodeURIComponent(shop)}`;
      if (host) nextPath += `&host=${encodeURIComponent(host)}`;
    }

    const oauthUrl = `${API}/auth/google?next=${encodeURIComponent(nextPath)}&linkToken=${encodeURIComponent(token)}`;

    if (isEmbedded) {
      window.open(oauthUrl, '_blank');
    } else {
      window.location.href = oauthUrl;
    }
  };

  const handleSaveAndInject = async () => {
    setProcessing(true);
    setGeneralError('');
    setInjectionStatus(null);
    setInjectionMessage('');

    try {
      // Only save the GA4 property if the user actually selected one
      if (propId) {
        const saveRes = await fetch(`${API}/integrations/property`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ propertyId: propId }),
        });
        if (!saveRes.ok) {
          const txt = await saveRes.text();
          throw new Error(`Failed to save property ID: ${txt}`);
        }
        setSavedPropId(propId);
      }

      let shopDomain =
        searchParams.get('shop') ||
        sessionStorage.getItem('shopifyShop') ||
        sessionStorage.getItem('shop') ||
        sessionStorage.getItem('shop_domain') ||
        localStorage.getItem('shopifyShop') ||
        localStorage.getItem('shop_domain') ||
        window.location.search.match(/[?&]shop=([^&]+)/)?.[1];

      if (!shopDomain) {
        try {
          const sitesRes = await fetch(`${API}/api/sites`, { headers: { Authorization: `Bearer ${token}` } });
          if (sitesRes.ok) {
            const sitesJson = await sitesRes.json();
            const sites = Array.isArray(sitesJson) ? sitesJson : sitesJson?.sites || sitesJson?.data || [];
            const shopifySite = sites.find((s) => s.platform?.toLowerCase() === 'shopify');
            if (shopifySite) {
              shopDomain = shopifySite.domain || shopifySite.url || shopifySite.shop_domain || shopifySite.shopDomain;
              if (shopDomain) {
                shopDomain = shopDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
                sessionStorage.setItem('shopifyShop', shopDomain);
                sessionStorage.setItem('shop_domain', shopDomain);
              }
            }
          }
        } catch (err) {
          console.error('[IntegrationsPage] Failed to fetch shop from API:', err);
        }
      }

      if (!shopDomain) {
        setGeneralError('Shop domain not found. Please reconnect your Shopify store.');
        setProcessing(false);
        return;
      }

      const injRes = await fetch(`${API}/integrations/inject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ propertyId: propId || null, shop: shopDomain }),
      });

      const injData = await injRes.json();
      if (!injRes.ok) {
        // Check for structured error: Shopify token expired/revoked
        const detail = injData.detail;
        const isTokenInvalid =
          (typeof detail === 'object' && detail?.code === 'SHOPIFY_TOKEN_INVALID') ||
          (typeof detail === 'string' && (
            detail.includes('Invalid API key or access token') ||
            detail.includes('unrecognized login')
          ));

        if (isTokenInvalid) {
          setInjectionStatus('token_expired');
          setInjectionMessage('');  // No raw error shown to user
          setGeneralError('');
          return;
        }

        // Generic fallback — never show raw API errors
        setInjectionStatus('critical');
        const friendlyMsg = typeof detail === 'object'
          ? (detail?.message || 'Something went wrong. Please try again.')
          : (typeof detail === 'string' && detail.length < 200 ? detail : 'Something went wrong. Please try again.');
        setInjectionMessage(friendlyMsg);
        setGeneralError('');
        return;
      }

      if (injData.detail?.includes('but existing property ID') && injData.detail?.includes('differs from requested')) {
        setInjectionStatus('warning');
        const existingProp = injData.detail.match(/existing property ID \((G-[^)]+)\)/)?.[1];
        setInjectionMessage(`Existing GA4 property (${existingProp}) detected. Please remove it manually from your theme before injecting a new one.`);
      } else if (injData.injected) {
        setInjectionStatus('success');
        setInjectionMessage('GA4 snippet successfully injected!');
        setShowSuccessModal(true);
        const urlParams = new URLSearchParams(window.location.search);
        const shop = urlParams.get('shop');
        if (shop) markShopSetupComplete(shop);
      } else {
        setInjectionStatus('success');
        setInjectionMessage(injData.detail || 'GA4 snippet already present with correct ID.');
        const urlParams = new URLSearchParams(window.location.search);
        const shop = urlParams.get('shop');
        if (shop) markShopSetupComplete(shop);
      }
    } catch (e) {
      setInjectionStatus('critical');
      setInjectionMessage('Something went wrong. Please check your connection and try again.');
      setGeneralError('');
      console.error('[IntegrationsPage] Injection error:', e);
    } finally {
      setProcessing(false);
    }
  };

  const handleDisconnectGA4 = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Google Analytics account? This will remove all saved GA4 settings.')) return;

    setDisconnecting(true);
    setGeneralError('');
    setInjectionStatus(null);
    setInjectionMessage('');

    try {
      let shopDomain =
        sessionStorage.getItem('shopifyShop') ||
        sessionStorage.getItem('shop_domain') ||
        sessionStorage.getItem('shop') ||
        localStorage.getItem('shopifyShop') ||
        localStorage.getItem('shop_domain') ||
        new URLSearchParams(window.location.search).get('shop');

      if (!shopDomain) {
        try {
          const sitesRes = await fetch(`${API}/api/sites`, { headers: { Authorization: `Bearer ${token}` } });
          if (sitesRes.ok) {
            const sitesJson = await sitesRes.json();
            const sites = Array.isArray(sitesJson) ? sitesJson : sitesJson?.sites || sitesJson?.data || [];
            const shopifySite = sites.find((s) => s.platform?.toLowerCase() === 'shopify');
            if (shopifySite) {
              shopDomain = shopifySite.domain || shopifySite.url || shopifySite.shop_domain || shopifySite.shopDomain;
              if (shopDomain) {
                shopDomain = shopDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
                sessionStorage.setItem('shopifyShop', shopDomain);
              }
            }
          }
        } catch (err) {
          console.error('[IntegrationsPage] Failed to fetch shop from API:', err);
        }
      }

      if (!shopDomain) throw new Error('Shop domain not found. Please refresh the page and try again.');

      const res = await fetch(`${API}/integrations/disconnect-ga4`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ shop: shopDomain }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Disconnect failed: ${txt}`);
      }

      resetIntegrationStates();

      const currentParams = new URLSearchParams(window.location.search);
      const shop = currentParams.get('shop') || sessionStorage.getItem('shopifyShop');
      const host = currentParams.get('host');
      const isInIframe = window.self !== window.top;
      const isEmbedded = !!(shop && (host || isInIframe));

      let nextPath = '/integrations';
      if (shop) {
        nextPath = `/integrations?shop=${encodeURIComponent(shop)}`;
        if (host) nextPath += `&host=${encodeURIComponent(host)}`;
      }

      const oauthUrl = `${API}/auth/google?next=${encodeURIComponent(nextPath)}` + (token ? `&linkToken=${encodeURIComponent(token)}` : '');

      if (isEmbedded) {
        window.open(oauthUrl, '_blank');
      } else {
        window.location.href = oauthUrl;
      }
    } catch (e) {
      setGeneralError(`Failed to disconnect: ${e.message}`);
      setDisconnecting(false);
    }
  };

  const handleReinstallShopify = () => {
    // Send user to Shopify install URL to refresh their token
    const shop =
      sessionStorage.getItem('shopifyShop') ||
      sessionStorage.getItem('shop_domain') ||
      new URLSearchParams(window.location.search).get('shop');

    if (shop) {
      // Best path: send them through your install URL (which triggers Shopify OAuth)
      const installUrl = `${API}/shopify/install?shop=${encodeURIComponent(shop)}`;
      window.open(installUrl, '_blank');
    } else {
      // Fallback: open Shopify App Store listing
      window.open('https://apps.shopify.com/baobrain', '_blank');
    }
  };

  const handleGoToDashboard = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const host = urlParams.get('host');
    const shop = urlParams.get('shop') || sessionStorage.getItem('shopifyShop');
    const isInIframe = window.self !== window.top;
    const isShopifyEmbedded = !!(host || (isInIframe && shop));

    if (isShopifyEmbedded && token) {
      const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'https://baobrain.com';
      const fullDashboardUrl = new URL('/embedded-login', FRONTEND_URL);
      fullDashboardUrl.searchParams.set('token', token);
      fullDashboardUrl.searchParams.set('redirect', '/dashboard');
      if (shop) {
        fullDashboardUrl.searchParams.set('shop', shop);
        fullDashboardUrl.searchParams.set('platform', 'shopify');
      }
      window.open(fullDashboardUrl.toString(), '_blank');
    } else {
      navigate('/dashboard');
    }
  };

  // Navigation
  const goNext = () => setSlideIndex((prev) => Math.min(prev + 1, 2));
  const goBack = () => setSlideIndex((prev) => Math.max(prev - 1, 0));

  // ============================================
  //  RENDER
  // ============================================

  if (status.loading) {
    return (
      <Page title="Set Up Analytics">
        <div className={styles.page}>
          <div className={styles.card}>
            <div className={styles.cardBody}>
              <p className={styles.statusText}>Loading...</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  // Step Tracker - based on actual progress, not just slideIndex
  // Install = always complete (they installed the app)
  // Connect = complete only if status.connected
  // Activate = complete only if isInjected
  const StepTracker = () => {
    const getStepStatus = (stepNum) => {
      if (stepNum === 1) return 'complete'; // Install always done
      if (stepNum === 2) return status.connected ? 'complete' : (slideIndex >= 1 ? 'current' : 'pending');
      if (stepNum === 3) return isInjected ? 'complete' : (slideIndex >= 2 ? 'current' : 'pending');
      return 'pending';
    };

    return (
      <div className={styles.stepTracker}>
        {/* Step 1: Install */}
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepIcon} ${styles.complete}`}>
            <CheckIcon />
          </div>
          <span className={`${styles.stepLabel} ${styles.complete}`}>Install</span>
        </div>

        <div className={styles.stepLine}>
          <div className={styles.stepLineProgress} style={{ width: status.connected ? '100%' : '0%' }} />
        </div>

        {/* Step 2: Connect */}
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepIcon} ${styles[getStepStatus(2)]}`}>
            {status.connected ? <CheckIcon /> : <AnalyticsIcon />}
          </div>
          <span className={`${styles.stepLabel} ${styles[getStepStatus(2)]}`}>Connect</span>
        </div>

        <div className={styles.stepLine}>
          <div className={styles.stepLineProgress} style={{ width: isInjected ? '100%' : '0%' }} />
        </div>

        {/* Step 3: Activate */}
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepIcon} ${styles[getStepStatus(3)]}`}>
            {isInjected ? <CheckIcon /> : <RocketIcon />}
          </div>
          <span className={`${styles.stepLabel} ${styles[getStepStatus(3)]}`}>Activate</span>
        </div>
      </div>
    );
  };

  // Card 1: Welcome/Onboarding
  const renderWelcomeSlide = () => (
    <div className={styles.slide}>
      <OnboardingBanner
        currentStep={slideIndex + 1}
        onNextStep={goNext}
        onDismiss={() => navigate('/dashboard')}
      />
    </div>
  );

  // Card 2: Connect Google + Select GA4 Property
  const renderGA4Slide = () => (
    <div className={styles.slide}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2>Add Google Analytics <span className={styles.optionalTag}>Optional</span></h2>
          <p>BaoBrain already tracks your store on its own. Linking GA4 layers in your historical Google data for deeper insights.</p>
        </div>

        <div className={styles.cardBody}>
          {!status.connected ? (
            <>
              <p className={styles.helpText}>
                If you already use Google Analytics, link it now so BaoBrain can pull your GA4 data in alongside its own tracking.
              </p>
              <button className={styles.btnPrimary} onClick={handleConnectGoogle}>
                <GoogleIcon /> Connect Google Account
              </button>
              <p className={styles.helpText}>
                Not using Google Analytics? Skip this step — BaoBrain's own tracking captures everything you need.
              </p>
            </>
          ) : propsLoading ? (
            <>
              <div className={styles.loadingSpinner} />
              <p className={styles.statusText}>Looking for your GA4 properties…</p>
              <p className={styles.helpText}>This usually takes a few seconds.</p>
            </>
          ) : (
            <>
              <div className={styles.connectedBadge}>
                <CheckIcon size={16} />
                <span>Google connected — GA4 data will layer in</span>
              </div>

              {propsList.length > 0 ? (
                <>
                  <div className={styles.selectGroup}>
                    <label htmlFor="ga4-property">Choose your Analytics property</label>
                    <select id="ga4-property" value={propId} onChange={(e) => setPropId(e.target.value)}>
                      {propsList.map((p) => (
                        <option key={p.measurementId} value={p.measurementId}>
                          {p.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className={styles.helpText}>
                    Don't see the right one?{' '}
                    <a href="https://analytics.google.com/" target="_blank" rel="noopener noreferrer">
                      Open Google Analytics
                    </a>
                  </p>
                </>
              ) : (
                <div className={styles.fallbackBox}>
                  <p className={styles.fallbackTitle}>No Analytics properties on this account</p>
                  <p className={styles.helpText}>
                    No problem — you can skip this step and still get full BaoBrain insights from our pixel.
                  </p>
                  <a
                    href="https://support.google.com/analytics/answer/9304153"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.fallbackLink}
                  >
                    Don't have Google Analytics? Set it up in 2 minutes →
                  </a>
                </div>
              )}

              {/* Quiet, secondary: only here for users who really need to switch accounts */}
              <button className={styles.btnText} onClick={handleDisconnectGA4} disabled={disconnecting}>
                {disconnecting ? 'Switching…' : 'Use a different Google account'}
              </button>
            </>
          )}

          {generalError && <p className={styles.errorText}>{generalError}</p>}
        </div>

        <div className={styles.cardFooter}>
          <button className={styles.btnSecondary} onClick={goBack}>
            <ArrowLeftIcon /> Back
          </button>
          {status.connected && !propsLoading && propsList.length === 0 ? (
            <button className={styles.btnPrimary} onClick={goNext}>
              Skip for now <ArrowRightIcon />
            </button>
          ) : (
            <button className={styles.btnPrimary} onClick={goNext} disabled={propsLoading}>
              {propId ? 'Next' : 'Skip for now'} <ArrowRightIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Card 3: Activate Tracking
  const renderActivateSlide = () => {
    const isTokenExpired = injectionStatus === 'token_expired';

    // Find the selected property's display name. Match by measurementId OR
    // any other field the backend might use, so we never fall through and
    // show a raw numeric ID to a non-technical user.
    const selectedProperty = propsList.find(
      (p) =>
        p.measurementId === propId ||
        p.propertyId === propId ||
        String(p.propertyId) === String(propId) ||
        String(p.measurementId) === String(propId)
    );
    // If we can't resolve a friendly name, just say "your Analytics property"
    // rather than dumping a numeric ID the user won't recognize.
    const propertyDisplayName = selectedProperty?.displayName || 'your Analytics property';

    return (
    <div className={styles.slide}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2>
            {isTokenExpired ? 'Reconnect your store' : isInjected ? " You've unlocked your insights" : 'Turn on tracking'}
          </h2>
          <p>
            {isTokenExpired
              ? "Your Shopify connection needs to be refreshed"
              : isInjected
              ? "BaoBrain is now watching every visitor, click, and conversion on your store"
              : 'One last step — turn on tracking for your Shopify store'}
          </p>
        </div>

        <div className={styles.cardBody}>
          {isTokenExpired ? (
            <>
              <div className={styles.warningIconWrap}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <p className={styles.helpText} style={{ maxWidth: 360 }}>
                It looks like BaoBrain was uninstalled from your Shopify store, or the connection expired. Reinstall to get tracking running again — it only takes a moment.
              </p>
            </>
          ) : isInjected ? (
            <>
              <div className={styles.successIcon}>
                <CheckIcon size={64} />
              </div>
              <ul className={styles.unlockList}>
                <li><CheckIcon size={16} /> Visitor tracking is live on your store</li>
                <li><CheckIcon size={16} /> Conversion analytics ready to capture orders</li>
                {propId && <li><CheckIcon size={16} /> GA4 connected for deeper traffic insights</li>}
                <li><CheckIcon size={16} /> Customer intelligence dashboards activated</li>
              </ul>
              <p className={styles.helpText} style={{ maxWidth: 360 }}>
                Visit your own store right now and you'll appear in your dashboard within seconds. Richer insights build up as more shoppers visit.
              </p>
            </>
          ) : (
            <>
              {propId ? (
                <div className={styles.summaryBox}>
                  <p className={styles.summaryLabel}>Connected to</p>
                  <p className={styles.summaryValue}>{propertyDisplayName}</p>
                </div>
              ) : (
                <p className={styles.helpText}>
                  You'll be tracking visitor behavior on your store with the BaoBrain pixel.
                </p>
              )}
              <p className={styles.helpText}>
                Click below and we'll set everything up automatically — no code needed.
              </p>

              {injectionStatus === 'warning' && (
                <div className={styles.inlineAlert}>
                  <span className={styles.inlineAlertIcon}>!</span>
                  <span>{injectionMessage}</span>
                </div>
              )}
              {injectionStatus === 'critical' && (
                <div className={styles.inlineAlertError}>
                  <span className={styles.inlineAlertIcon}>!</span>
                  <span>{injectionMessage}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.cardFooter}>
          {isTokenExpired ? (
            <>
              <button className={styles.btnSecondary} onClick={() => setInjectionStatus(null)}>
                <ArrowLeftIcon /> Back
              </button>
              <button className={styles.btnPrimary} onClick={handleReinstallShopify}>
                Reinstall BaoBrain
              </button>
            </>
          ) : isInjected ? (
            <>
              <button className={styles.btnSecondary} onClick={handleSaveAndInject} disabled={processing}>
                Re-run setup
              </button>
              <button className={styles.btnPrimary} onClick={handleGoToDashboard}>
                Go to Dashboard
              </button>
            </>
          ) : (
            <>
              <button className={styles.btnSecondary} onClick={goBack}>
                <ArrowLeftIcon /> Back
              </button>
              <button className={styles.btnPrimary} onClick={handleSaveAndInject} disabled={processing}>
                <RocketIcon /> {processing ? 'Setting up…' : 'Turn on tracking'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    );
  };

  return (
    <Page
      title="Set up your store"
      backAction={{ content: 'Back to Dashboard', onAction: () => navigate('/dashboard') }}
    >
      <div className={styles.page}>
        <StepTracker />

        {toast && (
          <div className={styles.toast} key={toast.key} role="status" aria-live="polite">
            <span className={styles.toastIcon}>
              <CheckIcon size={14} />
            </span>
            <span>{toast.msg}</span>
          </div>
        )}

        <div className={styles.carousel}>
          <div className={styles.track} style={{ transform: `translateX(-${slideIndex * 100}%)` }}>
            {renderWelcomeSlide()}
            {renderGA4Slide()}
            {renderActivateSlide()}
          </div>
        </div>

        <div className={styles.support}>
          Need help?{' '}
          <a href="https://app.baobrain.com/installation" target="_blank" rel="noopener noreferrer">Setup Guide</a>
          {' · '}
          <a href="/help" target="_blank" rel="noopener noreferrer">Contact Support</a>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className={styles.modalOverlay} onClick={() => setShowSuccessModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <FlashCardCongrats
              title=" You've unlocked your insights"
              message={
                <>
                  <p style={{ marginBottom: 12 }}>
                    BaoBrain is now watching every visitor on your store. Here's what you just turned on:
                  </p>
                  <ul style={{ paddingLeft: 20, margin: '0 0 12px', lineHeight: 1.7 }}>
                    <li>Real-time visitor & session tracking</li>
                    <li>Conversion funnel + drop-off detection</li>
                    <li>Customer intelligence dashboards</li>
                    {savedPropId && <li>GA4 historical data for deeper context</li>}
                  </ul>
                  <p style={{ marginTop: 12, fontSize: 13, color: '#6b7280' }}>
                    💡 <strong>Try it now:</strong> visit your own store in a new tab — you'll show up in your dashboard within seconds.
                  </p>
                  <p style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
                    Smarter pattern-based insights kick in after about <strong>24–72 hours</strong> of traffic.
                  </p>
                </>
              }
              ctaLabel="Take me to my dashboard"
              onCta={() => { setShowSuccessModal(false); handleGoToDashboard(); }}
              onClose={() => setShowSuccessModal(false)}
              onBackfill={async () => {
                try {
                  const siteIdToUse = siteId || Number(sessionStorage.getItem('site_id')) || Number(sessionStorage.getItem('shopify_site_id'));
                  if (!siteIdToUse) {
                    alert('Could not determine site ID. Please refresh the page and try again.');
                    return;
                  }
                  const res = await fetch(`${API}/api/customer-intelligence/backfill-orders?site_id=${siteIdToUse}&days=365`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  });
                  if (!res.ok) {
                    const errorData = await res.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Failed to start backfill');
                  }
                  alert('Historical order import started! This may take a few minutes.');
                  setShowSuccessModal(false);
                  navigate('/dashboard');
                } catch (error) {
                  alert('Failed to start backfill: ' + error.message);
                }
              }}
            />
          </div>
        </div>
      )}
    </Page>
  );
}